import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, OnOffSwitch, Select } from '@any-ui-react/core';
import { vestResolver } from '@hookform/resolvers/vest';

import { ChatType } from '@anyx/common-graphql';
import { AnyChatForm } from '@anyx/feature-crm';

import { validationSuite } from './AIAutoReplyConfigForm.validation';
import { InstructionPreview } from './InstructionPreview';

export type AiAutoReplyConfigFormData = {
  enabled?: boolean;
  storeId: string;
  chatType: ChatType;
  contextStoreName?: string | null;
  contextId?: string | null;
  hasAiFooter?: boolean;
};

export interface AiAutoReplyConfigFormProps {
  defaultValues?: AiAutoReplyConfigFormData;
  contextStoreOptions?: string[];
  contextOptions?: string[];
  systemInstructions?: string;
  onSelectContextStore?: (storeName: string | null) => void;
  onSelectContextId?: (id: string | null) => void;
  onSubmit: (data: AiAutoReplyConfigFormData) => void;
  onDisable?: () => void;
}

export const AiAutoReplyConfigForm = ({
  defaultValues,
  contextStoreOptions = [],
  contextOptions = [],
  systemInstructions,
  onSelectContextStore,
  onSelectContextId,
  onSubmit,
  onDisable,
}: AiAutoReplyConfigFormProps) => {
  const { t } = useTranslation(['shared']);

  const form = useForm<AiAutoReplyConfigFormData>({
    resolver: vestResolver(validationSuite),
    defaultValues,
  });

  const [enabled] = form.watch(['enabled']);

  return (
    <FormProvider {...form}>
      <AnyChatForm.Form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex items-center gap-6">
          <Controller
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <OnOffSwitch
                className="w-fit"
                labelPosition="left"
                checked={field.value}
                label={t('shared.field.aiAutoReply.name')}
                onChange={(e) => {
                  field.onChange(e);

                  if (!e.currentTarget.checked) {
                    onDisable?.();
                    form.clearErrors();
                    form.setValue('contextStoreName', null);
                    form.setValue('contextId', null);
                  }
                }}
              />
            )}
          />
          {enabled && (
            <Controller
              control={form.control}
              name="hasAiFooter"
              render={({ field }) => (
                <label className="flex cursor-pointer items-center gap-2">
                  <span>{t('shared.field.showAiLabel.name')}</span>
                  <Checkbox checked={field.value} onChange={field.onChange} />
                </label>
              )}
            />
          )}
        </div>

        {enabled && (
          <>
            <AnyChatForm.Block blockTitle={t('shared.field.contextStore.name')}>
              <Controller
                control={form.control}
                name="contextStoreName"
                render={({ field }) => (
                  <Select
                    {...field}
                    data={contextStoreOptions}
                    onChange={(value) => {
                      field.onChange(value);
                      form.setValue('contextId', null);
                      onSelectContextStore?.(value);
                    }}
                  />
                )}
              />
            </AnyChatForm.Block>
            <AnyChatForm.Block blockTitle={t('shared.field.context.name')}>
              <Controller
                control={form.control}
                name="contextId"
                render={({ field }) => (
                  <Select
                    {...field}
                    data={contextOptions}
                    onChange={(value) => {
                      field.onChange(value);
                      onSelectContextId?.(value);
                    }}
                    error={form.formState.errors.contextId?.message}
                  />
                )}
              />
              <InstructionPreview instruction={systemInstructions} />
            </AnyChatForm.Block>
            <AnyChatForm.ButtonGroup>
              <Button type="submit" variant="primary" loading={form.formState.isSubmitting}>
                {t('shared.button.save')}
              </Button>
            </AnyChatForm.ButtonGroup>
          </>
        )}
      </AnyChatForm.Form>
    </FormProvider>
  );
};
